import { createSlice } from "@reduxjs/toolkit";
import {
    createTdsDeclaration,
    fetchTdsDeclarations,
    fetchTdsDeclarationById,
    updateTdsDeclaration,
    deleteTdsDeclaration,
    uploadTdsDeclarationDocuments,
    fetchTdsDeclarationDocumentById,
    fetchTdsDeclarationsByEmployeeId,
} from "./tdsDeclarationAction";

interface TdsDeclarationState {
    declarations: any[];
    currentItem: any | null;
    documents: any[];
    employeeDeclarations: any[];
    loading: boolean;
    error: string | null;
    success: boolean;
}

const initialState: TdsDeclarationState = {
    declarations: [],
    currentItem: null,
    documents: [],
    employeeDeclarations: [],
    loading: false,
    error: null,
    success: false,
};

const tdsDeclarationSlice = createSlice({
    name: "tdsDeclaration",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // Create
        builder.addCase(createTdsDeclaration.pending, (state) => {
            state.loading = true;
            state.error = null;
            state.success = false;
        });
        builder.addCase(createTdsDeclaration.fulfilled, (state, action) => {
            state.loading = false;
            state.success = true;
            state.declarations.push(action.payload);
        });
        builder.addCase(createTdsDeclaration.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Fetch All
        builder.addCase(fetchTdsDeclarations.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchTdsDeclarations.fulfilled, (state, action) => {
            state.loading = false;
            state.declarations = action.payload;
        });
        builder.addCase(fetchTdsDeclarations.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Fetch By ID
        builder.addCase(fetchTdsDeclarationById.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchTdsDeclarationById.fulfilled, (state, action) => {
            state.loading = false;
            state.currentItem = action.payload;
        });
        builder.addCase(fetchTdsDeclarationById.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Update
        builder.addCase(updateTdsDeclaration.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(updateTdsDeclaration.fulfilled, (state, action) => {
            state.loading = false;
            state.success = true;
            state.declarations = state.declarations.map((item) =>
                item.id === action.payload.id ? action.payload : item
            );
        });
        builder.addCase(updateTdsDeclaration.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Delete
        builder.addCase(deleteTdsDeclaration.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(deleteTdsDeclaration.fulfilled, (state, action) => {
            state.loading = false;
            state.success = true;
            state.declarations = state.declarations.filter((item) => item.id !== action.meta.arg);
        });
        builder.addCase(deleteTdsDeclaration.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Upload Documents
        builder.addCase(uploadTdsDeclarationDocuments.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(uploadTdsDeclarationDocuments.fulfilled, (state, action) => {
            state.loading = false;
            state.success = true;
            state.documents.push(action.payload);
        });
        builder.addCase(uploadTdsDeclarationDocuments.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Fetch Document by ID
        builder.addCase(fetchTdsDeclarationDocumentById.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchTdsDeclarationDocumentById.fulfilled, (state, action) => {
            state.loading = false;
            state.documents = [action.payload];
        });
        builder.addCase(fetchTdsDeclarationDocumentById.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Fetch by Employee ID
        builder.addCase(fetchTdsDeclarationsByEmployeeId.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchTdsDeclarationsByEmployeeId.fulfilled, (state, action) => {
            state.loading = false;
            state.employeeDeclarations = action.payload.data;
        });
        builder.addCase(fetchTdsDeclarationsByEmployeeId.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });
    },
});

export default tdsDeclarationSlice.reducer;
